import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, User } from 'lucide-react';
import { useMe, useLogout, type MarketplaceUser } from '../api/use-marketplace';
import { LanguageSwitcher } from './language-switcher';
import { CountrySwitcher } from './country-switcher';
import { Button, cn, focusRing } from '../ui';

/** Salon-owner landing (CleverBooking). */
export const OWNERS_URL = 'https://clever-booking.com';
export const HELP_URL = 'https://help.cleverbooking.cloud';
const CONTACT_URL = 'mailto:support@clever-booking.com';
export const TERMS_URL = 'https://clever-booking.com/terms';
const PRIVACY_URL = 'https://clever-booking.com/privacy';

/** Page container: 1360 artboard with the spec's 56px desktop / 18px mobile gutters. */
const container = 'mx-auto w-full max-w-[1360px] px-gutter-m md:px-8 lg:px-gutter';

/**
 * - `default`: marketing nav + footer.
 * - `auth`: sign-in nav (logo left, "Browse salons" right), no footer — board 09.
 * - `minimal`: centred logo only, no footer — boards 10, 11, 12 and the
 *   confirmation ("Minimal nav" in design-spec §3).
 */
export type SiteShellVariant = 'default' | 'auth' | 'minimal';

export function SiteShell({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  variant?: SiteShellVariant;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-sl-bg">
      {variant === 'default' ? <SiteHeader /> : <FocusedHeader variant={variant} />}
      <main className="flex-1">{children}</main>
      {variant === 'default' && <SiteFooter />}
    </div>
  );
}

/** Auth / minimal header: same bar as the marketing nav, stripped down. */
function FocusedHeader({ variant }: { variant: 'auth' | 'minimal' }) {
  const { t } = useTranslation();
  return (
    <header className="border-b border-sl-line bg-sl-bg">
      <div
        className={cn(
          container,
          'flex h-nav-m items-center md:h-[77px]',
          variant === 'minimal' ? 'justify-center' : 'justify-between gap-4',
        )}
      >
        <Logo className="text-[22px] md:text-[27px]" />
        {variant === 'auth' && (
          // -mr-4 cancels the pill's px-4 so the label lines up with the
          // right gutter, mirroring the logo on the left gutter.
          <nav aria-label={t('nav.primary')} className="-mr-4">
            <NavLink to="/search" className={({ isActive }) => cn(navLinkClass({ isActive }), 'hit-44')}>
              {t('nav.browseSalons')}
            </NavLink>
          </nav>
        )}
      </div>
    </header>
  );
}

function Logo({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/"
      aria-label={t('nav.home')}
      className={cn(
        'rounded-sm font-display font-medium leading-none tracking-[.4px] text-sl-ink',
        focusRing,
        className,
      )}
    >
      Salunas
    </Link>
  );
}

const navLinkClass = ({ isActive }: { isActive?: boolean }) =>
  cn(
    'inline-flex h-ctl-sm items-center rounded-full px-4 text-[14.5px] font-medium text-sl-ink transition-colors',
    'hover:bg-sl-sand',
    focusRing,
    isActive && 'bg-sl-sand',
  );

/**
 * Marketing top nav (design-spec §3 "Top nav"): 76px desktop / 58px mobile,
 * bg, bottom line. Desktop: logo · Browse salons · For salon owners | city
 * pill · language pill · Sign in (or the account pill). Mobile keeps only the
 * language pill and Sign in / account, as drawn on board 13.
 */
export function SiteHeader() {
  const { t } = useTranslation();
  const me = useMe();
  const user = me.data;

  return (
    <header className="sticky top-0 z-40 border-b border-sl-line bg-sl-bg">
      <div className={cn(container, 'flex h-nav-m items-center justify-between gap-4 md:h-nav')}>
        <Logo className="text-[22px] md:text-[27px]" />
        <nav aria-label={t('nav.primary')} className="flex items-center gap-2">
          <div className="hidden items-center gap-1 md:flex">
            <NavLink to="/search" className={navLinkClass}>
              {t('nav.browseSalons')}
            </NavLink>
            <a href={OWNERS_URL} target="_blank" rel="noopener noreferrer" className={navLinkClass({})}>
              {t('nav.forOwners')}
            </a>
            <span aria-hidden="true" className="mx-3 h-6 w-px bg-sl-line" />
            <CountrySwitcher variant="pill" />
          </div>
          <LanguageSwitcher variant="pill" />
          {user ? (
            <UserMenu user={user} />
          ) : (
            <Button
              to="/login"
              variant="dark"
              size="sm"
              className="max-md:h-[34px] max-md:px-4 max-md:text-[13px]"
            >
              {t('common.signIn')}
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
}

/**
 * Signed-in account pill (spec "Account nav": person icon + email, outline,
 * 42px). A disclosure menu holds My bookings, Profile and Sign out so the
 * previous header's actions stay one click away. Icon-only on mobile.
 */
function UserMenu({ user }: { user: MarketplaceUser }) {
  const { t } = useTranslation();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const itemClass =
    'flex w-full items-center rounded-12 px-3.5 py-2.5 text-left text-[14px] font-medium text-sl-ink hover:bg-sl-sand focus-visible:bg-sl-sand focus-visible:outline-none';

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? 'account-menu' : undefined}
        aria-label={`${t('nav.accountMenu')}: ${user.email}`}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'hit-44 inline-flex h-[34px] items-center gap-2 rounded-full border border-sl-line-strong bg-transparent px-2.5 text-[14px] font-medium text-sl-ink transition-colors md:h-ctl-staff md:px-4',
          'hover:bg-sl-surface',
          focusRing,
        )}
      >
        <User className="h-4 w-4" aria-hidden="true" />
        <span className="hidden max-w-[220px] truncate md:inline">{user.email}</span>
        <ChevronDown className="hidden h-3.5 w-3.5 opacity-60 md:block" aria-hidden="true" />
      </button>
      {open && (
        <div
          id="account-menu"
          className="absolute right-0 top-full z-50 mt-2 w-56 rounded-16 border border-sl-line bg-sl-surface p-1.5 shadow-book-card"
        >
          <ul>
            <li>
              <Link to="/me/bookings" className={itemClass} onClick={() => setOpen(false)}>
                {t('common.myBookings')}
              </Link>
            </li>
            <li>
              <Link to="/me/profile" className={itemClass} onClick={() => setOpen(false)}>
                {t('nav.profile')}
              </Link>
            </li>
            <li className="md:hidden">
              <Link to="/search" className={itemClass} onClick={() => setOpen(false)}>
                {t('nav.browseSalons')}
              </Link>
            </li>
            <li className="mt-1 border-t border-sl-line-soft pt-1">
              <button
                type="button"
                className={cn(itemClass, 'text-sl-link')}
                onClick={() => {
                  setOpen(false);
                  logout.mutate();
                }}
              >
                {t('common.signOut')}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

const footerLinkClass =
  'rounded-sm text-[13.5px] text-sl-on-dark-chip transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sl-hero-accent';

/**
 * Footer (design-spec §3 "Footer"): #1C140C, logo + blurb left, EXPLORE and
 * SALUNAS columns right; a divider, then copyright/legal left and the country
 * domain pill + language row right. Stacks on mobile.
 */
export function SiteFooter() {
  const { t } = useTranslation();
  const me = useMe();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-sl-footer text-sl-on-dark-muted">
      <div className={cn(container, 'pb-[34px] pt-[42px]')}>
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
          <div className="max-w-[340px]">
            <p className="font-display text-[24px] font-normal leading-none text-sl-on-dark">Salunas</p>
            <p className="mt-4 text-[13.5px] leading-[1.6]">
              {t('footer.tagline')}{' '}
              <a
                href={OWNERS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-sm text-sl-hero-accent underline decoration-sl-hero-accent/50 underline-offset-2 hover:decoration-sl-hero-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sl-hero-accent"
              >
                {t('footer.builtBy')}
              </a>
              .
            </p>
          </div>
          <div className="flex gap-16 md:gap-[84px]">
            <FooterColumn title={t('footer.explore')}>
              <Link to="/search" className={footerLinkClass}>
                {t('nav.browseSalons')}
              </Link>
              <Link to="/search" className={footerLinkClass}>
                {t('footer.categories')}
              </Link>
              <Link to={me.data ? '/me/bookings' : '/login'} className={footerLinkClass}>
                {t('common.myBookings')}
              </Link>
            </FooterColumn>
            <FooterColumn title={t('footer.salunas')}>
              <a href={OWNERS_URL} target="_blank" rel="noopener noreferrer" className={footerLinkClass}>
                {t('common.forSalons')}
              </a>
              <a href={HELP_URL} target="_blank" rel="noopener noreferrer" className={footerLinkClass}>
                {t('footer.helpCentre')}
              </a>
              <a href={CONTACT_URL} className={footerLinkClass}>
                {t('footer.contact')}
              </a>
            </FooterColumn>
          </div>
        </div>

        <div className="mt-9 flex flex-col gap-5 border-t border-sl-on-dark-muted/20 pt-6 md:flex-row md:items-center md:justify-between">
          <p className="text-[12.5px]">
            {t('footer.copyright', { year })}
            <span aria-hidden="true"> · </span>
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-sl-on-dark-chip">
              {t('footer.terms')}
            </a>
            <span aria-hidden="true"> · </span>
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-sl-on-dark-chip">
              {t('footer.privacy')}
            </a>
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <CountrySwitcher variant="domain" />
            <LanguageSwitcher variant="inline" />
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[1.6px] text-sl-on-dark-faint">{title}</p>
      <div className="mt-3.5 flex flex-col items-start gap-2.5">{children}</div>
    </div>
  );
}

export function formatAddress(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    const a = JSON.parse(raw) as { street?: string; city?: string; postalCode?: string };
    const parts = [a.street, [a.postalCode, a.city].filter(Boolean).join(' ').trim()].filter(Boolean);
    return parts.length ? parts.join(', ') : raw;
  } catch {
    return raw;
  }
}

export function categoryEmoji(key: string): string {
  switch (key) {
    case 'hair':    return '✂︎';
    case 'barber':  return '✦';
    case 'nails':   return '♢';
    case 'beauty':  return '✿';
    case 'massage': return '♨︎';
    case 'spa':     return '❀';
    default:        return '✦';
  }
}

export function subdomainHashHue(subdomain: string): number {
  return Array.from(subdomain).reduce(
    (acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 360,
    0,
  );
}
